// helpers/chat/conversationCache.ts
import type { QueryClient } from 'react-query'

export function conversationKey(conversationId: string | null, searchTerm = '') {
  return ['conversation', conversationId, searchTerm.trim()] as const
}

function safePages(old: any): { pages: any[]; pageParams: any[] } | null {
  if (!old || !Array.isArray(old.pages)) return null
  return { pages: old.pages, pageParams: old.pageParams ?? [] }
}

function ensureFirstPage(pages: any[]) {
  if (pages.length > 0) return pages
  return [{ data: [], meta: {} }]
}

function getServerMessageId(row: any): string | null {
  const sid = row?.message?.id ?? null

  if (sid == null) return null

  const s = String(sid).trim()

  if (!s || s === '0') return null

  return s
}
function isBlobUrl(value: any) {
  return typeof value === 'string' && value.startsWith('blob:')
}

function isDataUrl(value: any) {
  return typeof value === 'string' && value.startsWith('data:')
}

function isLocalPreviewUrl(value: any) {
  return isBlobUrl(value) || isDataUrl(value)
}

function getMediaUrl(item: any): string | null {
  return (
    item?.previewUrl ||
    item?.src ||
    item?.url ||
    item?.path ||
    item?.thumb_url ||
    item?.imagePreview ||
    item?.active_thumb ||
    item?.storageUrl ||
    item?.serverUrl ||
    item?.preview?.src ||
    null
  )
}

function getAllVisualMedia(message: any): any[] {
  return [
    ...(Array.isArray(message?.photos_preview) ? message.photos_preview : []),
    ...(Array.isArray(message?.videos_preview) ? message.videos_preview : []),
    ...(Array.isArray(message?.sounds) ? message.sounds : []),
    ...(Array.isArray(message?.photos) ? message.photos : []),
    ...(Array.isArray(message?.videos) ? message.videos : []),
    ...(Array.isArray(message?.__client_media_preview)
      ? message.__client_media_preview
      : []),
  ]
}

function getClientVisualMedia(message: any): any[] {
  return getAllVisualMedia(message).filter((item: any) => {
    const url = getMediaUrl(item)
    return isLocalPreviewUrl(url)
  })
}

function hasLocalPreview(items: any) {
  if (!Array.isArray(items)) return false

  return items.some((item: any) => {
    const url = getMediaUrl(item)
    return isLocalPreviewUrl(url)
  })
}

function hasAnyVisualMedia(items: any) {
  if (!Array.isArray(items)) return false

  return items.some((item: any) => {
    const url = getMediaUrl(item)
    return typeof url === 'string' && url.length > 0
  })
}

function mergeMediaKeepLocalPreview(localItems: any, serverItems: any) {
  const localIsArray = Array.isArray(localItems)
  const serverIsArray = Array.isArray(serverItems)

  if (!serverIsArray && !localIsArray) {
    return serverItems
  }

  if (!serverIsArray && localIsArray) {
    return localItems
  }

  if (serverIsArray && !localIsArray) {
    return serverItems
  }

  const localHasLocalPreview = hasLocalPreview(localItems)
  const localHasAnyVisualMedia = hasAnyVisualMedia(localItems)

  if (localHasLocalPreview) {
    return localItems.map((localItem: any, index: number) => {
      const serverItem = serverItems[index]
      const localUrl = getMediaUrl(localItem)

      if (!isLocalPreviewUrl(localUrl)) return localItem

      return {
        ...serverItem,
        ...localItem,

        previewUrl: localUrl,
        src: localUrl,

        serverUrl:
          serverItem?.serverUrl ||
          serverItem?.url ||
          serverItem?.src ||
          serverItem?.path ||
          localItem?.serverUrl ||
          null,

        storageUrl:
          serverItem?.storageUrl ||
          serverItem?.url ||
          serverItem?.path ||
          localItem?.storageUrl ||
          null,

        id: serverItem?.id ?? localItem?.id,
        status: serverItem?.status ?? localItem?.status,

        __usingOptimisticBlob: true,
      }
    })
  }

  if (localHasAnyVisualMedia && (!serverItems || serverItems.length === 0)) {
    return localItems
  }

  return serverItems
}

function mergeMessageKeepOptimisticMedia(localMessage: any, serverMessage: any) {
  const local = localMessage ?? {}
  const server = serverMessage ?? {}

  const clientVisualMediaBefore = getClientVisualMedia(local)

  return {
    ...local,
    ...server,

    photos_preview: mergeMediaKeepLocalPreview(
      local.photos_preview,
      server.photos_preview
    ),

    videos_preview: mergeMediaKeepLocalPreview(
      local.videos_preview,
      server.videos_preview
    ),

    sounds: mergeMediaKeepLocalPreview(local.sounds, server.sounds),

    photos: mergeMediaKeepLocalPreview(local.photos, server.photos),

    videos: mergeMediaKeepLocalPreview(local.videos, server.videos),

    __client_media_preview:
      clientVisualMediaBefore.length > 0
        ? clientVisualMediaBefore
        : Array.isArray(local.__client_media_preview)
          ? local.__client_media_preview
          : [],

    id: server.id ?? local.id,
    created_at: server.created_at ?? local.created_at,
    sender_id: server.sender_id ?? local.sender_id,
    user_id: server.user_id ?? local.user_id,
    is_sender: server.is_sender ?? local.is_sender,
    is_purchased: server.is_purchased ?? local.is_purchased,
    price: server.price ?? local.price,
  }
}

function removeServerMessageIdEverywhere(pages: any[], serverMessageId: string) {
  return pages.map((p) => {
    const data = Array.isArray(p?.data) ? p.data : []
    const next = data.filter(
      (r: any) => String(getServerMessageId(r)) !== serverMessageId
    )

    return next.length === data.length ? p : { ...p, data: next }
  })
}

export function replaceOptimisticWithServer(
  queryClient: QueryClient,
  conversationId: string,
  tempId: string,
  serverRow: any
) {
  const serverMessageId = getServerMessageId(serverRow)

  queryClient.setQueriesData(['conversation', conversationId], (old: any) => {
    const base = safePages(old)
    if (!base) return old

    let pages = ensureFirstPage([...base.pages])

    if (serverMessageId) {
      pages = removeServerMessageIdEverywhere(pages, serverMessageId)
    }

    let replaced = false

    pages = pages.map((p) => {
      const data = Array.isArray(p?.data) ? p.data : []

      const next = data.map((r: any) => {
        if (String(r?.id) !== String(tempId)) return r

        replaced = true

        const mergedMessage = mergeMessageKeepOptimisticMedia(
          r?.message,
          serverRow?.message
        )

        return {
          ...r,
          ...serverRow,
          sending: false,
          id: r.id,
          message: mergedMessage,
          __client_debug: {
            ...(r?.__client_debug ?? {}),
            ...(serverRow?.__client_debug ?? {}),
            replacedOptimistic: true,
            replacedAt: new Date().toISOString(),
            tempId,
            serverMessageId: serverMessageId ?? null,
            keptWrapperId: r.id,
            serverWrapperId: serverRow?.id ?? null,
            keptLocalPreviewCount: getClientVisualMedia(r?.message).length,
          },
        }
      })

      return next === data ? p : { ...p, data: next }
    })

    if (!replaced) {
      const first = pages[0]
      const firstData = Array.isArray(first.data) ? [...first.data] : []

      firstData.unshift({
        ...serverRow,
        sending: false,
        __client_debug: {
          ...(serverRow?.__client_debug ?? {}),
          insertedNoTempFound: true,
          insertedAt: new Date().toISOString(),
          tempId,
          serverMessageId: serverMessageId ?? null,
        },
      })

      pages[0] = { ...first, data: firstData }
    }

    return { ...old, pages }
  })
}